import React, { useState } from 'react';
import {
  Button,
  IconButton,
  ViewIconButton,
  EditIconButton,
  DeleteIconButton,
  CancelIconButton,
  RefreshIconButton,
  DownloadIconButton,
  UploadIconButton,
  CopyIconButton,
  MoreIconButton,
  AddIconButton,
  ApproveIconButton,
  CloseIconButton,
  SearchIconButton,
  FilterIconButton,
  SendIconButton,
  SettingsIconButton,
  ExternalLinkIconButton,
  ShareIconButton,
  Input,
  Select,
  Textarea,
  Modal,
  Card,
  Avatar,
  AvatarStack,
  Badge,
  ProgressBar,
  Table,
  Calendar,
  Pagination,
  MultiSelect,
} from '../../components/ui';
import { Heart } from 'lucide-react';

/* ── inline styles (dev-only page, no module needed) ── */
const sg = {
  page: {
    maxWidth: 960,
    margin: '0 auto',
    padding: '120px 24px 80px',
    fontFamily: 'system-ui, sans-serif',
  },
  heading: { borderBottom: '2px solid #e2e8f0', paddingBottom: 8, marginTop: 48 },
  row: { display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 16 },
  label: { fontSize: 12, color: '#64748b', marginBottom: 4 },
  cell: { display: 'flex', flexDirection: 'column' },
  sectionNote: { fontSize: 13, color: '#94a3b8', marginBottom: 16 },
};

/* ── helpers ── */
const Section = ({ title, note, children }) => (
  <>
    <h2 style={sg.heading}>{title}</h2>
    {note && <p style={sg.sectionNote}>{note}</p>}
    {children}
  </>
);

const Label = ({ children }) => <span style={sg.label}>{children}</span>;

const Row = ({ label, children }) => (
  <div style={{ marginBottom: 24 }}>
    {label && <Label>{label}</Label>}
    <div style={sg.row}>{children}</div>
  </div>
);

/* ================================================================ */
/*  Styleguide page                                                  */
/* ================================================================ */
const Styleguide = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [singleDate, setSingleDate] = useState('');
  const [rangeDate, setRangeDate] = useState(['', '']);
  const [page, setPage] = useState(1);
  const [multiValue, setMultiValue] = useState(['react']);
  const multiOptions = [
    { value: 'react', label: 'React', sublabel: 'A JavaScript library for building UIs' },
    { value: 'vue', label: 'Vue', sublabel: 'The progressive framework' },
    { value: 'angular', label: 'Angular', sublabel: 'Platform for building mobile/desktop apps' },
    { value: 'svelte', label: 'Svelte', sublabel: 'Cybernetically enhanced web apps' },
    { value: 'solid', label: 'Solid', sublabel: 'Simple and performant reactivity' },
    { value: 'qwik', label: 'Qwik', sublabel: 'Instant-loading web apps' },
  ];

  return (
    <div style={sg.page}>
      <h1>Styleguide — UI Primitives</h1>
      <p style={sg.sectionNote}>
        Dev-only reference. Every component from <code>src/components/ui/</code> rendered in every
        variant, size, and state.
      </p>

      {/* ─── Button ─── */}
      <Section title="Button">
        <Row label="Variants × sizes">
          {['primary', 'secondary', 'ghost', 'danger'].map((v) =>
            ['sm', 'md', 'lg'].map((s) => (
              <Button key={`${v}-${s}`} variant={v} size={s}>
                {v} {s}
              </Button>
            ))
          )}
        </Row>

        <Row label="Disabled">
          {['primary', 'secondary', 'ghost', 'danger'].map((v) => (
            <Button key={v} variant={v} disabled>
              {v}
            </Button>
          ))}
        </Row>

        <Row label="Loading">
          {['primary', 'secondary', 'ghost', 'danger'].map((v) => (
            <Button key={v} variant={v} loading>
              {v}
            </Button>
          ))}
        </Row>

        <Row label="With icons">
          <Button leftIcon={<span>◀</span>}>Left icon</Button>
          <Button rightIcon={<span>▶</span>}>Right icon</Button>
          <Button leftIcon={<span>◀</span>} rightIcon={<span>▶</span>}>
            Both icons
          </Button>
        </Row>

        <Row label="Hover demo (use cursor)">
          {['primary', 'secondary', 'ghost', 'danger'].map((v) => (
            <Button key={v} variant={v}>
              Hover me ({v})
            </Button>
          ))}
        </Row>
      </Section>

      {/* ─── IconButton ─── */}
      <Section
        title="IconButton"
        note="Square icon-only action buttons. Use the pre-configured presets (ViewIconButton, EditIconButton, …) for consistency, or compose your own with <IconButton icon={<…/>} intent={…} />."
      >
        <Row label="Presets (default size = sm, variant = soft)">
          <ViewIconButton onClick={() => {}} />
          <EditIconButton onClick={() => {}} />
          <DeleteIconButton onClick={() => {}} />
          <CancelIconButton onClick={() => {}} />
          <RefreshIconButton onClick={() => {}} />
          <DownloadIconButton onClick={() => {}} />
          <UploadIconButton onClick={() => {}} />
          <CopyIconButton onClick={() => {}} />
          <AddIconButton onClick={() => {}} />
          <ApproveIconButton onClick={() => {}} />
          <SendIconButton onClick={() => {}} />
          <ShareIconButton onClick={() => {}} />
          <SearchIconButton onClick={() => {}} />
          <FilterIconButton onClick={() => {}} />
          <SettingsIconButton onClick={() => {}} />
          <ExternalLinkIconButton onClick={() => {}} />
          <CloseIconButton onClick={() => {}} />
          <MoreIconButton onClick={() => {}} />
        </Row>

        <Row label="Sizes (xs · sm · md · lg)">
          {['xs', 'sm', 'md', 'lg'].map((s) => (
            <ViewIconButton key={s} size={s} tooltip={`size=${s}`} />
          ))}
        </Row>

        <Row label="Variants × intents (variant: soft)">
          {['primary', 'info', 'success', 'warning', 'danger', 'neutral'].map((i) => (
            <IconButton
              key={`soft-${i}`}
              icon={<Heart />}
              intent={i}
              variant="soft"
              tooltip={`soft / ${i}`}
            />
          ))}
        </Row>

        <Row label="Variant: solid">
          {['primary', 'info', 'success', 'warning', 'danger', 'neutral'].map((i) => (
            <IconButton
              key={`solid-${i}`}
              icon={<Heart />}
              intent={i}
              variant="solid"
              tooltip={`solid / ${i}`}
            />
          ))}
        </Row>

        <Row label="Variant: ghost">
          {['primary', 'info', 'success', 'warning', 'danger', 'neutral'].map((i) => (
            <IconButton
              key={`ghost-${i}`}
              icon={<Heart />}
              intent={i}
              variant="ghost"
              tooltip={`ghost / ${i}`}
            />
          ))}
        </Row>

        <Row label="Variant: outline">
          {['primary', 'info', 'success', 'warning', 'danger', 'neutral'].map((i) => (
            <IconButton
              key={`outline-${i}`}
              icon={<Heart />}
              intent={i}
              variant="outline"
              tooltip={`outline / ${i}`}
            />
          ))}
        </Row>

        <Row label="Pill (circular)">
          <ViewIconButton pill size="md" />
          <EditIconButton pill size="md" />
          <DeleteIconButton pill size="md" />
          <ApproveIconButton pill size="md" variant="solid" />
        </Row>

        <Row label="States">
          <ViewIconButton tooltip="Disabled" disabled />
          <DeleteIconButton tooltip="Loading" loading />
        </Row>
      </Section>

      {/* ─── Input ─── */}
      <Section title="Input">
        <Row label="Default">
          <Input placeholder="Default input" />
        </Row>
        <Row label="With label">
          <Input label="Email" placeholder="you@example.com" />
        </Row>
        <Row label="With helper text">
          <Input label="Username" helperText="Must be at least 3 characters" placeholder="jdoe" />
        </Row>
        <Row label="Error">
          <Input label="Email" error="Invalid email address" placeholder="bad@" />
        </Row>
        <Row label="Disabled">
          <Input label="Disabled" placeholder="Cannot edit" disabled />
        </Row>
        <Row label="With icons">
          <Input leftIcon={<span>🔍</span>} placeholder="Search..." />
          <Input rightIcon={<span>✓</span>} placeholder="Validated" />
        </Row>
      </Section>

      {/* ─── Select ─── */}
      <Section title="Select">
        <Row label="Default">
          <Select label="Country">
            <option value="">Choose…</option>
            <option value="us">United States</option>
            <option value="uk">United Kingdom</option>
          </Select>
        </Row>
        <Row label="Error">
          <Select label="Country" error="Selection required">
            <option value="">Choose…</option>
          </Select>
        </Row>
        <Row label="Disabled">
          <Select label="Country" disabled>
            <option>Locked</option>
          </Select>
        </Row>
        <Row label="With helper text">
          <Select label="Region" helperText="Select your nearest region">
            <option value="">Choose…</option>
            <option value="east">East</option>
            <option value="west">West</option>
          </Select>
        </Row>
      </Section>

      {/* ─── Textarea ─── */}
      <Section title="Textarea">
        <Row label="Default">
          <Textarea label="Notes" placeholder="Type here..." />
        </Row>
        <Row label="Error">
          <Textarea label="Bio" error="Too short" placeholder="Tell us about yourself" />
        </Row>
        <Row label="Disabled">
          <Textarea label="Read-only" placeholder="Cannot edit" disabled />
        </Row>
        <Row label="With helper text">
          <Textarea label="Comments" helperText="Markdown supported" placeholder="Write…" />
        </Row>
      </Section>

      {/* ─── Modal ─── */}
      <Section title="Modal" note="Click the button to open each size.">
        <Row label="Sizes">
          {['sm', 'md', 'lg'].map((s) => (
            <Button key={s} variant="secondary" size="sm" onClick={() => setModalOpen(s)}>
              Open {s}
            </Button>
          ))}
        </Row>
        {['sm', 'md', 'lg'].map((s) => (
          <Modal
            key={s}
            show={modalOpen === s}
            onHide={() => setModalOpen(false)}
            title={`Modal — ${s}`}
            size={s}
            footer={
              <Button variant="primary" size="sm" onClick={() => setModalOpen(false)}>
                Close
              </Button>
            }
          >
            <p>This is a <strong>{s}</strong> modal.</p>
          </Modal>
        ))}
      </Section>

      {/* ─── Card ─── */}
      <Section title="Card">
        <Row label="Padding">
          {['sm', 'md', 'lg'].map((p) => (
            <Card key={p} padding={p}>
              padding="{p}"
            </Card>
          ))}
        </Row>
        <Row label="Elevation">
          {['sm', 'md', 'lg'].map((e) => (
            <Card key={e} elevation={e}>
              elevation="{e}"
            </Card>
          ))}
        </Row>
        <Row label="Combined">
          <Card padding="lg" elevation="lg">
            padding="lg" elevation="lg"
          </Card>
        </Row>
      </Section>

      {/* ─── Avatar ─── */}
      <Section title="Avatar">
        <Row label="Sizes (with initials)">
          {['xs', 'sm', 'md', 'lg'].map((s) => (
            <Avatar key={s} name="Jane Doe" size={s} />
          ))}
        </Row>
        <Row label="Status indicators">
          {['online', 'offline', 'busy', 'away'].map((st) => (
            <div key={st} style={sg.cell}>
              <Avatar name="JD" status={st} />
              <Label>{st}</Label>
            </div>
          ))}
        </Row>
        <Row label="No src / no name (fallback)">
          <Avatar />
        </Row>
      </Section>

      {/* ─── AvatarStack ─── */}
      <Section title="AvatarStack">
        <Row label="Default (max 3)">
          <AvatarStack
            avatars={[
              { name: 'Alice' },
              { name: 'Bob' },
              { name: 'Carol' },
              { name: 'Dan' },
              { name: 'Eve' },
            ]}
          />
        </Row>
        <Row label="Sizes">
          {['xs', 'sm', 'md', 'lg'].map((s) => (
            <div key={s} style={sg.cell}>
              <AvatarStack
                size={s}
                avatars={[{ name: 'A' }, { name: 'B' }, { name: 'C' }, { name: 'D' }]}
                max={3}
              />
              <Label>{s}</Label>
            </div>
          ))}
        </Row>
      </Section>

      {/* ─── Badge ─── */}
      <Section title="Badge">
        <Row label="Variants × sizes">
          {['neutral', 'success', 'warning', 'danger', 'info'].map((v) =>
            ['sm', 'md'].map((s) => (
              <Badge key={`${v}-${s}`} variant={v} size={s}>
                {v} {s}
              </Badge>
            ))
          )}
        </Row>
      </Section>

      {/* ─── ProgressBar ─── */}
      <Section title="ProgressBar">
        <Row label="Variants at 60%">
          {['primary', 'success', 'warning', 'danger'].map((v) => (
            <div key={v} style={{ width: 200 }}>
              <Label>{v}</Label>
              <ProgressBar value={60} variant={v} />
            </div>
          ))}
        </Row>
        <Row label="Values (with label)">
          {[0, 25, 50, 75, 100].map((val) => (
            <div key={val} style={{ width: 160 }}>
              <ProgressBar value={val} showLabel />
            </div>
          ))}
        </Row>
      </Section>

      {/* ─── Calendar ─── */}
      <Section title="Calendar">
        <Row label="Single date">
          <Calendar
            label="Pick a date"
            value={singleDate}
            onChange={setSingleDate}
            placeholder="Select date"
          />
        </Row>
        <Row label="Date range">
          <Calendar
            mode="range"
            label="Pick a range"
            value={rangeDate}
            onChange={setRangeDate}
            placeholder="Select range"
          />
        </Row>
        <Row label="With helper text">
          <Calendar
            label="Start date"
            value={singleDate}
            onChange={setSingleDate}
            helperText="Used for report generation"
          />
        </Row>
        <Row label="Error">
          <Calendar
            label="Required date"
            value=""
            onChange={() => {}}
            error="Please select a date"
          />
        </Row>
        <Row label="Disabled">
          <Calendar label="Locked" value="2026-01-01" disabled />
        </Row>
        <Row label="Min/Max bounds">
          <Calendar
            label="Within Apr 2026"
            value={singleDate}
            onChange={setSingleDate}
            minDate="2026-04-01"
            maxDate="2026-04-30"
          />
        </Row>
      </Section>

      {/* ─── Pagination ─── */}
      <Section title="Pagination">
        <Row label="With info (default)">
          <Pagination
            page={page}
            pageCount={10}
            onPageChange={setPage}
            total={97}
            itemLabel="users"
          />
        </Row>
        <Row label="Without info">
          <Pagination page={page} pageCount={10} onPageChange={setPage} showInfo={false} />
        </Row>
        <Row label="Few pages">
          <Pagination page={page > 3 ? 1 : page} pageCount={3} onPageChange={setPage} total={24} />
        </Row>
        <Row label="Many pages">
          <Pagination page={page} pageCount={50} onPageChange={setPage} total={1234} />
        </Row>
      </Section>

      {/* ─── MultiSelect ─── */}
      <Section title="MultiSelect">
        <Row label="Default">
          <div style={{ width: 320 }}>
            <MultiSelect
              label="Frameworks"
              value={multiValue}
              onChange={setMultiValue}
              options={multiOptions}
              placeholder="Pick frameworks"
            />
          </div>
        </Row>
        <Row label="With helper text">
          <div style={{ width: 320 }}>
            <MultiSelect
              label="Frameworks"
              value={multiValue}
              onChange={setMultiValue}
              options={multiOptions}
              helperText="You can pick more than one"
            />
          </div>
        </Row>
        <Row label="Error">
          <div style={{ width: 320 }}>
            <MultiSelect
              label="Frameworks"
              value={[]}
              onChange={() => {}}
              options={multiOptions}
              error="Select at least one"
            />
          </div>
        </Row>
        <Row label="Disabled">
          <div style={{ width: 320 }}>
            <MultiSelect
              label="Frameworks"
              value={['react', 'vue']}
              onChange={() => {}}
              options={multiOptions}
              disabled
            />
          </div>
        </Row>
        <Row label="Loading">
          <div style={{ width: 320 }}>
            <MultiSelect
              label="Frameworks"
              value={[]}
              onChange={() => {}}
              options={[]}
              loading
            />
          </div>
        </Row>
        <Row label="Empty options">
          <div style={{ width: 320 }}>
            <MultiSelect
              label="Frameworks"
              value={[]}
              onChange={() => {}}
              options={[]}
              emptyMessage="Nothing here yet"
            />
          </div>
        </Row>
      </Section>

      {/* ─── Table ─── */}
      <Section title="Table">
        {[
          { label: 'Default', props: {} },
          { label: 'Striped', props: { striped: true } },
          { label: 'Hoverable', props: { hoverable: true } },
          { label: 'Compact', props: { compact: true } },
          { label: 'All options', props: { striped: true, hoverable: true, compact: true } },
        ].map(({ label, props }) => (
          <div key={label} style={{ marginBottom: 24 }}>
            <Label>{label}</Label>
            <Table {...props}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>Alice</td><td>Engineer</td><td>Active</td></tr>
                <tr><td>Bob</td><td>Designer</td><td>Away</td></tr>
                <tr><td>Carol</td><td>PM</td><td>Offline</td></tr>
              </tbody>
            </Table>
          </div>
        ))}
      </Section>
    </div>
  );
};

export default Styleguide;
